import { z } from "zod";
import { dayjs } from "../dayjs/index.js";
import { looseUuid } from "../types/index.js";
/**
 * Field schemas a product's forms build on.
 *
 * Validation messages are translation keys rather than sentences, so each
 * product supplies the wording from its own locale. Nothing here knows which
 * product it is serving; a schema that would is one for that product's domain
 * and belongs there instead.
 */
// Pinned rather than left to dayjs's formatting defaults, so the string this module
// emits cannot shift under a dependency upgrade.
const UTC_DATE_TIME_FORMAT = "YYYY-MM-DDTHH:mm:ss[Z]";
/**
 * A valid date and time
 */
export const dateTimeSchema = z.iso.datetime("fieldSchema.dateTime.invalid");
/**
 * A valid UTC date and time which is not in the future.
 * We provide a tolerance of when "now" is, due to
 * - Issues with timezones being +/- 1 hour
 * - Issues with multi-minute clock drift on the client device.
 *
 * `abort` stops the not-in-the-future check running on a value that never
 * parsed, which would report one typo as two contradictory problems.
 */
export const nonFutureDateTimeSchema = z.iso
    .datetime({ error: "fieldSchema.dateTime.invalid", abort: true })
    .refine((dateTime) => dayjs.utc(dateTime).isBefore(dayjs().add(1, "hour").add(5, "minutes")), "fieldSchema.dateTime.invalidInFuture");
/**
 * A valid date
 */
export const dateSchema = z.iso.date({ error: "fieldSchema.date.invalid" });
/**
 * A valid localtime date which is not in the future
 * We provide a tolerance of when "now" is, due to
 * - Issues with timezones being +/- 1 hour
 * - Issues with multi-minute clock drift on the client device.
 *
 * `abort` is load-bearing rather than tidy, for a sharper reason than on
 * `nonFutureDateTimeSchema`: the refine below calls `dayjs.tz()`, which throws on
 * an unparseable date instead of returning an invalid one.
 */
export const nonFutureDateSchema = z.iso
    .date({ error: "fieldSchema.date.invalid", abort: true })
    .refine((date) => dayjs
    .tz(date, "Europe/London")
    .isBefore(dayjs().add(1, "hour").add(5, "minutes")), "fieldSchema.date.invalidInFuture");
/**
 * A valid date or UTC date-time which is not in the future.
 * Dates are coerced to midday in Europe/London and then emitted as UTC date-time.
 * We provide a tolerance of when "now" is, due to
 * - Issues with timezones being +/- 1 hour
 * - Issues with multi-minute clock drift on the client device.
 */
export const nonFutureDateOrDateTimeSchema = z
    .string()
    .refine((value) => dateSchema.safeParse(value).success ||
    dateTimeSchema.safeParse(value).success, "fieldSchema.dateTime.invalid")
    .transform((value) => {
    if (dateSchema.safeParse(value).success) {
        return dayjs
            .tz(value, "Europe/London")
            .hour(12)
            .minute(0)
            .second(0)
            .millisecond(0)
            .utc()
            .format(UTC_DATE_TIME_FORMAT);
    }
    return value;
})
    .refine((dateTime) => dayjs.utc(dateTime).isBefore(dayjs().add(1, "hour").add(5, "minutes")), "fieldSchema.dateTime.invalidInFuture");
/**
 * A stringified time
 */
export const timeSchema = z
    .string()
    .regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/, "fieldSchema.time.invalid");
/**
 * A free text field allowing up to 5,000 characters
 */
export const longTextSchema = z.string().min(1).max(5000);
/**
 * A typical free text field that will fit in a database column
 */
export const shortTextSchema = z.string().min(1).max(255);
/**
 * A person's forename
 */
export const forenameSchema = shortTextSchema;
/**
 * A person's surname
 */
export const surnameSchema = shortTextSchema;
/**
 * An address
 */
export const addressSchema = longTextSchema;
/**
 * A postal code
 */
export const postalCodeSchema = z.string().min(1).max(8);
export const nameSchema = z.string().min(1).max(255);
export const orgIdSchema = looseUuid();
export const emailSchema = z.email();
/**
 * A yes/no field
 */
export const yesNoSchema = z.enum(["yes", "no"]);
/**
 * A yes only field
 */
export const yesOnlySchema = z.enum(["yes"]);
export const yesNoUnknownSchema = z.enum(["yes", "no", "unknown"]);
//# sourceMappingURL=fieldSchemas.js.map